<template>
  <!-- Spacer reserves the header's height while it is pinned (fixed) so the
       content below never jumps as the header slides in/out on scroll. -->
  <div v-if="pinningEnabled" aria-hidden="true" :style="{ height: `${headerHeight}px` }" />

  <!--
    When pinned the header is `fixed` and slides in on scroll up / out on scroll
    down by animating `transform: translateY` (not `top`). Animating `top` is a
    layout property, so every frame during scroll registers a layout shift and
    tallies into CLS; `transform` is excluded from CLS. Because a non-`none`
    transform turns the header into a containing block and would break the mobile
    drawer's `fixed inset-0`, `headerTransform` falls back to `none` while the
    drawer is open (header is fully revealed then, so it looks identical).
    z-50 keeps it above the sticky context tab menu (z-40), which shifts down
    via shared sticky offset while the header is revealed.
  -->
  <div
    ref="headerRootRef"
    :class="pinningEnabled
      ? 'fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ease-out'
      : 'relative'"
    :style="pinningEnabled ? { transform: headerTransform } : null"
  >
    <!-- ============================ MOBILE HEADER (< lg) ============================ -->
    <div class="lg:hidden bg-linear-to-b from-[#f0f9ff] to-white border-b border-[#e5e7eb] w-full">
      <!-- Phone row -->
      <div class="flex items-center justify-center px-4 py-3">
        <ContactPhoneRow class="py-3! text-code-medical-center">
          <p class="font-sans leading-none">
            <a
              v-if="phoneNumber"
              :href="`tel:${phoneNumber}`"
              class="text-link mr-1 no-underline text-[18px] leading-[22px] font-normal"
            >{{ phoneNumber }}</a><span class="text-gray text-[18px] leading-[22px] font-normal">{{ phoneHours }}</span>
          </p>
        </ContactPhoneRow>
      </div>

      <!-- Logo + actions -->
      <div class="flex items-center justify-between gap-3 px-4 pb-3 pt-2">
        <Link href="/" class="flex items-center shrink-0">
          <div class="h-[48px] w-[138px] relative">
            <img
              alt="Logo"
              class="absolute inset-0 max-w-none object-cover object-center pointer-events-none w-full h-full"
              :src="logoImage"
            >
          </div>
        </Link>

        <div class="flex items-center gap-2">
          <a
            :href="bookAppointmentUrl || '#'"
            class="flex h-[40px] items-center justify-center px-[16px] rounded-[8px] shadow-[0px_2px_6px_0px_rgba(16,24,40,0.06)] no-underline"
            style="background-image: linear-gradient(160.691deg, rgb(46, 168, 207) 0%, rgb(40, 182, 155) 100%)"
          >
            <span class="text-white text-[14px] font-sans font-semibold leading-[16px] whitespace-nowrap">{{ bookAppointmentName }}</span>
          </a>
          <button
            type="button"
            class="relative border border-code-medical-center-nav flex items-center justify-center rounded-[8px] w-[40px] h-[40px] text-code-medical-center-nav cursor-pointer transition-colors hover:bg-code-medical-center-nav/5 p-2"
            :aria-label="t('common.open_menu')"
            @click="openMobileMenu"
          >
            <svg class="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 6h18M3 12h18M3 18h18" />
            </svg>
            <!-- Badge when "Twoje wizyty" has active visits (Figma 8029:13403) -->
            <span
              v-if="visitsCount"
              class="absolute top-[-5px] right-[-3px] size-[12px] rounded-full bg-code-medical-center-nav shadow-[0px_0px_0px_4px_rgba(12,74,110,0.1)]"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </div>

    <!-- ============================ MOBILE MENU DRAWER (< lg) ============================ -->
    <div v-if="showMobileMenu" class="lg:hidden fixed inset-0 z-60 flex flex-col bg-white">
      <!-- Drawer top bar -->
      <div class="bg-linear-to-b from-[#f0f9ff] to-white border-b border-[#e5e7eb] w-full shrink-0">
        <div class="flex items-center justify-center px-4 py-3">
          <ContactPhoneRow class="py-3! text-code-medical-center">
            <p class="font-sans leading-none">
              <a
                v-if="phoneNumber"
                :href="`tel:${phoneNumber}`"
                class="text-link mr-1 no-underline text-[18px] leading-[22px] font-normal"
              >{{ phoneNumber }}</a><span class="text-gray text-[18px] leading-[22px] font-normal">{{ phoneHours }}</span>
            </p>
          </ContactPhoneRow>
        </div>
        <div class="flex items-center justify-between gap-3 px-4 pb-3 pt-2">
          <Link href="/" class="flex items-center shrink-0" @click="closeMobileMenu">
            <div class="h-[48px] w-[138px] relative">
              <img
                alt="Logo"
                class="absolute inset-0 max-w-none object-cover object-center pointer-events-none w-full h-full"
                :src="logoImage"
              >
            </div>
          </Link>
          <div class="flex items-center gap-3">
            <a
              :href="bookAppointmentUrl || '#'"
              class="flex h-[40px] items-center justify-center px-[16px] rounded-[8px] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.04)] no-underline"
              style="background-image: linear-gradient(161.125deg, rgb(46, 168, 207) 0%, rgb(40, 182, 155) 100%)"
            >
              <span class="text-white text-[14px] font-sans font-medium leading-[16px] whitespace-nowrap">{{ bookAppointmentName }}</span>
            </a>
            <button
              type="button"
              class="border border-code-medical-center-nav flex items-center justify-center rounded-[8px] w-[40px] h-[40px] text-code-medical-center-nav cursor-pointer transition-colors hover:bg-code-medical-center-nav/5 p-2"
              aria-label="Zamknij menu"
              @click="closeMobileMenu"
            >
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Drawer scrollable body -->
      <div ref="mobileScrollRef" class="flex-1 overflow-y-auto px-4 py-4">
        <!-- ===== MAIN VIEW ===== -->
        <template v-if="mobileView === 'main'">
          <!-- Accessibility + language -->
          <div class="flex items-start justify-between gap-3 mb-4">
            <AccessibilityWidget inline />

            <!-- Language Selector Dropdown (same behaviour as desktop) -->
            <div ref="mobileLanguageDropdownRef" class="relative shrink-0">
              <button
                type="button"
                class="flex gap-[8px] items-center justify-center overflow-hidden px-[16px] py-[10px] h-[40px] rounded-[8px] hover:bg-code-medical-center/5 transition-colors cursor-pointer"
                @click="toggleLanguageDropdown"
                @dblclick.prevent
              >
                <span class="bg-white overflow-hidden w-[20px] shrink-0">
                  <img alt="Current locale flag" class="block max-w-none w-full h-full" :src="currentLocaleFlag">
                </span>
                <span class="text-code-medical-center-nav text-[14px] font-sans font-medium leading-[16px]">{{ currentLocale.toUpperCase() }}</span>
                <span class="overflow-hidden relative w-[20px] shrink-0">
                  <img alt="" class="block max-w-none w-full h-full" :src="chevronDownIcon">
                </span>
              </button>

              <div
                v-if="showLanguageDropdown"
                class="absolute top-full right-0 mt-1 bg-white rounded-[8px] shadow-lg z-50 min-w-[120px] overflow-hidden border border-[#e5e7eb]"
              >
                <button
                  v-for="locale in availableLocales"
                  :key="locale.code"
                  type="button"
                  :class="[
                    'w-full flex gap-[8px] items-center px-[16px] py-[10px] hover:bg-gray-50 transition-colors text-left',
                    currentLocale === locale.code ? 'bg-gray-50' : ''
                  ]"
                  @click="switchLocale(locale.code)"
                >
                  <span class="bg-white overflow-hidden w-[20px] shrink-0">
                    <img alt="Locale flag" class="block max-w-none w-full h-full" :src="locale.flag">
                  </span>
                  <span class="text-code-medical-center-nav text-[14px] font-sans font-medium leading-[16px]">{{ locale.code.toUpperCase() }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Twoje wizyty + clinic buttons -->
          <div class="flex flex-col gap-3 mb-4">
            <Link
              href="/wizyty"
              class="btn-nav-outline flex h-[48px] w-full items-center justify-center gap-[8px] overflow-clip rounded-[8px] no-underline shrink-0"
              @click="closeMobileMenu"
            >
              <span class="text-code-medical-center-nav text-[16px] font-sans font-semibold leading-[18px] whitespace-nowrap">
                {{ t('booking.history_title') }}
              </span>
              <span
                v-if="visitsCount"
                class="bg-code-medical-center-nav flex size-[20px] shrink-0 items-center justify-center overflow-clip rounded-full p-[2px] shadow-[0px_0px_0px_4px_rgba(12,74,110,0.1)]"
              >
                <span class="text-center text-[10px] font-sans font-medium leading-[14px] text-white whitespace-nowrap">
                  {{ visitsCount }}
                </span>
              </span>
            </Link>

            <Link
              href="/klinika-przyczynowego-leczenia-nieplodnosci"
              :class="[
                'flex items-center w-full px-5 py-3.5 rounded-[8px] text-[16px] font-sans font-medium leading-[18px] no-underline transition-all',
                currentSiteContext === 'infertility_treatment_clinic'
                  ? 'bg-infertility-clinic-secondary border border-infertility-clinic shadow-sm text-[#262626]'
                  : 'bg-white border border-[#e5e7eb] text-code-medical-center-nav'
              ]"
              @click="closeMobileMenu"
            >
              {{ infertilityClinicName }}
            </Link>
            <Link
              href="/klinika-zdrowia-rodziny"
              :class="[
                'flex items-center w-full px-5 py-3.5 rounded-[8px] text-[16px] font-sans font-medium leading-[18px] no-underline transition-all',
                currentSiteContext === 'family_clinic'
                  ? 'bg-family-clinic-secondary border border-family-clinic shadow-sm text-[#262626]'
                  : 'bg-white border border-[#e5e7eb] text-code-medical-center-nav'
              ]"
              @click="closeMobileMenu"
            >
              {{ familyClinicName }}
            </Link>
            <Link
              href="/instytut"
              :class="[
                'flex items-center w-full px-5 py-3.5 rounded-[8px] text-[16px] font-sans font-medium leading-[18px] no-underline transition-all',
                currentSiteContext === 'institute'
                  ? 'bg-institute-secondary border border-institute shadow-sm text-[#262626]'
                  : 'bg-white border border-[#e5e7eb] text-code-medical-center-nav'
              ]"
              @click="closeMobileMenu"
            >
              {{ instituteName }}
            </Link>
          </div>

          <!-- Service / specialist / specialisation nav -->
          <div class="flex flex-col pt-2">
            <button
              v-for="item in serviceNavItems"
              :key="item.category"
              type="button"
              class="flex items-center justify-between w-full px-2 py-4 text-left cursor-pointer"
              @click="openMobileService(item.category)"
            >
              <span class="text-code-medical-center-nav text-[18px] font-sans font-medium leading-[18px] no-underline">{{ item.label }}</span>
              <svg class="w-5 h-5 text-code-medical-center-nav shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button
              type="button"
              class="flex items-center justify-between w-full px-2 py-4 text-left cursor-pointer"
              @click="openMobileSpecialists"
            >
              <span class="text-code-medical-center-nav text-[18px] font-sans font-medium leading-[18px] no-underline">{{ specialistsName }}</span>
              <svg class="w-5 h-5 text-code-medical-center-nav shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button
              type="button"
              class="flex items-center justify-between w-full px-2 py-4 text-left cursor-pointer"
              @click="openMobileSpecializations"
            >
              <span class="text-code-medical-center-nav text-[18px] font-sans font-medium leading-[18px] no-underline">{{ specializationsName }}</span>
              <svg class="w-5 h-5 text-code-medical-center-nav shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <!-- Aktualności / Kontakt -->
          <div class="flex flex-col">
            <a
              :href="aktualnosciUrl"
              class="px-2 py-4 text-code-medical-center-nav text-[18px] font-sans font-medium leading-[18px] no-underline hover:no-underline"
              @click="closeMobileMenu"
            >{{ aktualnosciName }}</a>
            <a
              :href="contactUrl || '#'"
              class="px-2 py-4 text-code-medical-center-nav text-[18px] font-sans font-medium leading-[18px] no-underline hover:no-underline"
              @click="closeMobileMenu"
            >{{ contactName }}</a>
          </div>

          <!-- Location -->
          <div class="flex items-center gap-2 px-2 pt-6 pb-4">
            <div class="relative w-[20px] h-[20px] shrink-0">
              <img alt="" class="block max-w-none w-full h-full" :src="locationIcon" aria-hidden="true">
            </div>
            <p class="leading-[18px] text-[16px] text-code-medical-center-nav font-sans">
              <span>CODE Centrum Medyczne </span>
              <span class="text-gray">(ul. Tuwima 15/U4, 90-010 Łódź)</span>
            </p>
          </div>
        </template>

        <!-- ===== SERVICE DRILL-DOWN ===== -->
        <template v-else-if="mobileView === 'service'">
          <button
            type="button"
            class="flex items-center gap-2 px-2 py-3 mb-2 text-code-medical-center-nav cursor-pointer"
            @click="mobileView = 'main'"
          >
            <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            <span class="text-[18px] font-sans font-semibold leading-[18px] no-underline">{{ mobileServiceLabel }}</span>
          </button>

          <SpecialisationFilterList
            scrollable
            show-all-option
            :specialisations="specialisationsForFilter"
            :model-value="selectedServiceSpecialisationId"
            :loading="loadingSpecialisations"
            variant="header"
            @update:model-value="mobileFilterServices"
          />

          <Link
            :href="mobileServicesIndexHref"
            class="btn-nav-outline mb-4 flex h-12 w-full items-center justify-center rounded-lg font-sans text-[16px] font-semibold leading-[18px] no-underline"
            @click="closeMobileMenu"
          >
            <span>{{ mobileServiceViewAllLabel }}</span>
            <Icon name="cheveron-right" class="ml-1 size-4 shrink-0 fill-current" />
          </Link>

          <div v-if="loadingServices && filteredServices.length === 0" class="flex w-full items-center justify-center py-10 text-gray">
            <p>{{ t('common.loading') }}</p>
          </div>
          <div v-else-if="filteredServices.length > 0" class="flex flex-col gap-3">
            <Link
              v-for="service in filteredServices"
              :key="service.id"
              :href="`${servicesUrl}/${service.slug}`"
              class="bg-white flex flex-col gap-[12px] items-start rounded-[12px] border border-code-medical-center-secondary p-[16px] no-underline"
              @click="closeMobileMenu"
            >
              <!-- TODO: przywrócić po migracji relacji usługa → specjalista
              <div v-if="service.specialisations?.length" class="flex flex-wrap gap-[8px] items-start">
                <SpecialisationBadge
                  v-for="spec in service.specialisations"
                  :key="spec.id"
                  :name="spec.name"
                  :site-context="spec.siteContext || spec.site_context"
                />
              </div>
              -->
              <p class="font-sans font-semibold text-dark text-[18px] leading-[26px] tracking-[-0.4px]">{{ service.name }}</p>
            </Link>
          </div>
          <div v-else class="flex w-full items-center justify-center py-10 text-gray">
            <p>{{ t('services.empty_hint') }}</p>
          </div>
        </template>

        <!-- ===== SPECIALISTS DRILL-DOWN ===== -->
        <template v-else-if="mobileView === 'specialists'">
          <button
            type="button"
            class="flex items-center gap-2 px-2 py-3 mb-2 text-code-medical-center-nav cursor-pointer"
            @click="mobileView = 'main'"
          >
            <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            <span class="text-[18px] font-sans font-semibold leading-[18px] no-underline">{{ specialistsName }}</span>
          </button>

          <SpecialisationFilterList
            scrollable
            show-all-option
            :specialisations="specialisationsForFilter"
            :model-value="selectedSpecialisationId"
            :loading="loadingSpecialisations"
            variant="header"
            @update:model-value="mobileFilterSpecialists"
          />

          <Link
            :href="siecialistUrl"
            class="btn-nav-outline mb-4 flex h-12 w-full items-center justify-center rounded-lg font-sans text-[16px] font-semibold leading-[18px] no-underline"
            @click="closeMobileMenu"
          >
            <span>{{ t('specialists.view_all') }}</span>
            <Icon name="cheveron-right" class="ml-1 size-4 shrink-0 fill-current" />
          </Link>

          <div v-if="loadingSpecialists && filteredSpecialists.length === 0" class="flex w-full items-center justify-center py-10 text-gray">
            <p>{{ t('common.loading') }}</p>
          </div>
          <div v-else-if="filteredSpecialists.length > 0" class="flex flex-col gap-4">
            <SpecialistCard
              v-for="specialist in filteredSpecialists"
              :key="specialist.id"
              :specialist="specialist"
              :specialists-base-url="siecialistUrl"
              clickable-card
              @click="closeMobileMenu"
            />
          </div>
          <div v-else class="flex w-full items-center justify-center py-10 text-gray">
            <p>{{ t('specialists.empty_hint') }}</p>
          </div>
        </template>

        <!-- ===== SPECIALISATIONS DRILL-DOWN ===== -->
        <template v-else-if="mobileView === 'specializations'">
          <button
            type="button"
            class="flex items-center gap-2 px-2 py-3 mb-2 text-code-medical-center-nav cursor-pointer"
            @click="mobileView = 'main'"
          >
            <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            <span class="text-[18px] font-sans font-semibold leading-[18px] no-underline">{{ specializationsName }}</span>
          </button>

          <Link
            :href="specialisationsUrl"
            class="btn-nav-outline mb-4 flex h-12 w-full items-center justify-center rounded-lg font-sans text-[16px] font-semibold leading-[18px] no-underline"
            @click="closeMobileMenu"
          >
            <span>{{ t('specialisations.view_all') }}</span>
            <Icon name="cheveron-right" class="ml-1 size-4 shrink-0 fill-current" />
          </Link>

          <div v-if="loadingSpecialisations && allSpecialisations.length === 0" class="flex w-full items-center justify-center py-10 text-gray">
            <p>{{ t('common.loading') }}</p>
          </div>
          <div v-else class="grid grid-cols-2 gap-3">
            <Link
              v-for="specialisation in allSpecialisations"
              :key="specialisation.id"
              :href="`${specialisationsUrl}/${specialisation.slug}`"
              class="bg-white flex min-w-0 items-center px-[12px] py-[12px] rounded-[12px] shadow-[0px_2px_6px_0px_rgba(16,24,40,0.06)] no-underline"
              @click="closeMobileMenu"
            >
              <p class="min-w-0 break-words font-sans font-semibold text-dark text-[14px] leading-[20px]">{{ specialisation.name }}</p>
            </Link>
          </div>
        </template>
      </div>
    </div>

    <div class="bg-white border-b border-[#e5e7eb] hidden lg:flex flex-col items-center justify-center py-0 w-full">
      <div class="max-w-[1200px] w-full mx-auto">

        <!-- Top Bar -->
        <div class="flex flex-col items-center justify-center">
          <div class="flex items-center justify-between w-full">

            <!-- Left content -->
            <div class="flex gap-[24px] items-center">

              <!-- Location Link -->
              <div class="flex gap-[8px] items-center justify-center overflow-hidden px-0 py-[14px] rounded-[8px]">
                <div class="overflow-hidden relative w-[20px] h-[20px]">
                  <img
                    alt="Location icon"
                    class="block max-w-none w-full h-full"
                    :src="locationIcon"
                  >
                </div>
                <div class="flex flex-col justify-center leading-0 text-code-medical-center-nav text-[0px] text-center whitespace-nowrap">
                  <p class="leading-[18px] text-[16px]">
                    <span>CODE Centrum Medyczne </span>
                    <span class="text-gray">(ul. Tuwima 15/U4, 90-010 Łódź)</span>
                  </p>
                </div>
              </div>

              <!-- Phone Button -->
              <ContactPhoneRow class="h-12 px-0 text-code-medical-center-nav">
                <p class="text-base leading-[18px] font-sans font-medium">
                  <span style="font-weight: 400;">{{ phoneLabel }}&nbsp;</span>
                  <a
                    v-if="phoneNumber"
                    :href="`tel:${phoneNumber}`"
                    style="font-weight: 600"
                    class="text-link mr-1 no-underline"
                  >{{ phoneNumber }}</a><span style="font-weight: 400;" class="text-gray">{{ phoneHours }}</span>
                </p>
              </ContactPhoneRow>
            </div>

            <!-- Right content -->
            <div class="flex items-center">
              <!-- Language Selector Dropdown -->
              <div ref="languageDropdownRef" class="relative">
                <button
                  class="flex gap-[8px] items-center justify-center overflow-hidden px-[16px] py-[10px] rounded-[8px] hover:bg-white/50 transition-colors cursor-pointer"
                  @click="toggleLanguageDropdown"
                  @dblclick.prevent
                >
                  <div class="bg-white overflow-hidden w-[20px]">
                    <img
                      alt="Current locale flag"
                      class="block max-w-none w-full h-full"
                      :src="currentLocaleFlag"
                    >
                  </div>
                  <div class="flex flex-col justify-center leading-0 text-code-medical-center-nav text-[14px] text-center whitespace-nowrap font-sans font-medium">
                    <p class="leading-snug">{{ currentLocale.toUpperCase() }}</p>
                  </div>
                  <div class="overflow-hidden relative w-[20px]">
                    <img
                      alt="Chevron down icon"
                      class="block max-w-none w-full h-full"
                      :src="chevronDownIcon"
                    >
                  </div>
                </button>

                <!-- Dropdown Menu -->
                <div
                  v-if="showLanguageDropdown"
                  class="absolute top-full right-0 mt-1 bg-white rounded-[8px] shadow-lg z-50 min-w-[120px] overflow-hidden"
                >
                  <button
                    v-for="locale in availableLocales"
                    :key="locale.code"
                    :class="[
                      'w-full flex gap-[8px] items-center px-[16px] py-[10px] hover:bg-gray-50 transition-colors text-left',
                      currentLocale === locale.code ? 'bg-gray-50' : ''
                    ]"
                    @click="switchLocale(locale.code)"
                  >
                    <div class="bg-white overflow-hidden w-[20px] shrink-0">
                      <img
                        alt="Locale flag"
                        class="block max-w-none w-full h-full"
                        :src="locale.flag"
                      >
                    </div>
                    <div class="flex flex-col justify-center leading-0 text-code-medical-center-nav text-[14px] whitespace-nowrap font-sans font-medium">
                      <p class="leading-[16px]">{{ locale.code.toUpperCase() }}</p>
                    </div>
                  </button>
                </div>
              </div>

              <!-- Aktualności Link -->
              <a :href="aktualnosciUrl" class="flex gap-[8px] h-[40px] items-center justify-center overflow-hidden px-[16px] py-[10px] rounded-[8px]">
                <div class="flex flex-col justify-center leading-0 text-code-medical-center-nav text-[14px] text-center whitespace-nowrap font-sans font-medium">
                  <p class="leading-[16px]">{{ aktualnosciName }}</p>
                </div>
              </a>

              <!-- Kontakt Link -->
              <a
                :href="contactUrl || '#'"
                class="flex gap-[8px] h-[40px] items-center justify-center overflow-hidden px-[16px] py-[10px] rounded-[8px] no-underline"
              >
                <div class="flex flex-col justify-center leading-0 text-code-medical-center-nav text-[14px] text-center whitespace-nowrap font-sans font-medium">
                  <p class="leading-[16px]">{{ contactName }}</p>
                </div>
              </a>
            </div>
          </div>
        </div>

        <!-- Logo and Clinic Links Row -->
        <div class="flex flex-col items-center justify-center px-0 py-[16px] w-full">
          <div class="flex items-center justify-between w-full">
            <!-- Logo -->
            <Link href="/" class="flex items-center">
              <div class="h-[64px] w-[178px] relative">
                <img
                  alt="Logo"
                  class="absolute inset-0 max-w-none object-cover object-center pointer-events-none w-full h-full"
                  :src="logoImage"
                >
              </div>
            </Link>

            <!-- Clinic Links -->
            <div class="flex gap-[12px] items-start">
              <Link
                href="/klinika-przyczynowego-leczenia-nieplodnosci"
                :class="[
                  'flex flex-col justify-center leading-0 text-[16px] whitespace-nowrap font-sans font-medium gap-[8px] h-[48px] items-center overflow-hidden px-[20px] py-[14px] rounded-[8px] transition-all',
                  currentSiteContext === 'infertility_treatment_clinic'
                    ? 'bg-infertility-clinic-secondary border border-infertility-clinic shadow-sm text-[#262626]'
                    : 'bg-white border border-[#e5e7eb] hover:border-code-medical-center hover:shadow-sm text-code-medical-center-nav'
                ]"
              >
                <p class="leading-[18px]">{{ infertilityClinicName }}</p>
              </Link>

              <Link
                href="/klinika-zdrowia-rodziny"
                :class="[
                  'flex flex-col justify-center leading-0 text-[16px] whitespace-nowrap font-sans font-medium gap-[8px] h-[48px] items-center overflow-hidden px-[20px] py-[14px] rounded-[8px] transition-all',
                  currentSiteContext === 'family_clinic'
                    ? 'bg-family-clinic-secondary border border-family-clinic shadow-sm text-[#262626]'
                    : 'bg-white border border-[#e5e7eb] hover:border-code-medical-center hover:shadow-sm text-code-medical-center-nav'
                ]"
              >
                <p class="leading-[18px]">{{ familyClinicName }}</p>
              </Link>

              <Link
                href="/instytut"
                :class="[
                  'flex flex-col justify-center leading-0 text-[16px] whitespace-nowrap font-sans font-medium gap-[8px] h-[48px] items-center overflow-hidden px-[20px] py-[14px] rounded-[8px] transition-all',
                  currentSiteContext === 'institute'
                    ? 'bg-institute-secondary border border-institute shadow-sm text-[#262626]'
                    : 'bg-white border border-[#e5e7eb] hover:border-code-medical-center hover:shadow-sm text-code-medical-center-nav'
                ]"
              >
                <p class="leading-[18px]">{{ instituteName }}</p>
              </Link>
            </div>
          </div>
        </div>

        <!-- Navigation Menu Row -->
        <div class="flex flex-col items-center justify-center px-0 py-[16px] w-full">
          <div class="flex items-center justify-between w-full">
            <!-- Left Navigation Links -->
            <div ref="mainNavRef" class="flex items-center">
              <div class="flex items-start">
                <button
                  v-for="item in serviceNavItems"
                  :key="item.category"
                  type="button"
                  class="flex gap-[8px] h-[48px] items-center justify-center overflow-hidden px-[20px] py-[14px] rounded-[8px] hover:bg-white/50 transition-colors cursor-pointer"
                  @click.stop="toggleServiceDropdown(item.category)"
                  @dblclick.prevent
                >
                  <div class="flex flex-col justify-center leading-0 text-code-medical-center-nav text-[16px] text-center whitespace-nowrap font-sans font-medium">
                    <p class="leading-[18px]">{{ item.label }}</p>
                  </div>
                  <div class="overflow-hidden relative w-[20px] h-[20px]">
                    <img
                      alt=""
                      class="block max-w-none w-full h-full transition-transform duration-200"
                      :class="{ 'rotate-180': activeServiceCategory === item.category }"
                      :src="chevronDownIcon"
                    >
                  </div>
                </button>

                <!-- Specjaliści -->
                <button
                  type="button"
                  class="flex gap-[8px] h-[48px] items-center justify-center overflow-hidden px-[20px] py-[14px] rounded-[8px] hover:bg-white/50 transition-colors cursor-pointer"
                  @click.stop="toggleSpecialistsDropdown"
                  @dblclick.prevent
                >
                  <div class="flex flex-col justify-center leading-0 text-code-medical-center-nav text-[16px] text-center whitespace-nowrap font-sans font-medium">
                    <p class="leading-[18px]">{{ specialistsName }}</p>
                  </div>
                  <div class="overflow-hidden relative w-[20px] h-[20px]">
                    <img
                      alt=""
                      class="block max-w-none w-full h-full transition-transform duration-200"
                      :class="{ 'rotate-180': showSpecialistsDropdown }"
                      :src="chevronDownIcon"
                    >
                  </div>
                </button>

                <!-- Specjalizacje -->
                <button
                  type="button"
                  class="flex gap-[8px] h-[48px] items-center justify-center overflow-hidden px-[20px] py-[14px] rounded-[8px] hover:bg-white/50 transition-colors cursor-pointer"
                  @click.stop="toggleSpecializationsDropdown"
                  @dblclick.prevent
                >
                  <div class="flex flex-col justify-center leading-0 text-code-medical-center-nav text-[16px] text-center whitespace-nowrap font-sans font-medium">
                    <p class="leading-[18px]">{{ specializationsName }}</p>
                  </div>
                  <div class="overflow-hidden relative w-[20px] h-[20px]">
                    <img
                      alt=""
                      class="block max-w-none w-full h-full transition-transform duration-200"
                      :class="{ 'rotate-180': showSpecializationsDropdown }"
                      :src="chevronDownIcon"
                    >
                  </div>
                </button>
              </div>
            </div>

            <!-- Right Action Buttons -->
            <div class="flex gap-[32px] items-center">
              <!-- Twoje wizyty (Figma 7999:9436, Sky/900 #0c4a6e). Link na sztywno
                   do /wizyty; licznik z localStorage (getBookings). -->
              <Link
                href="/wizyty"
                class="btn-nav-outline flex h-[48px] items-center justify-center gap-[8px] overflow-clip rounded-[8px] no-underline shrink-0"
              >
                <span class="text-code-medical-center-nav text-[16px] font-sans font-semibold leading-[18px] whitespace-nowrap">
                  {{ t('booking.history_title') }}
                </span>
                <span
                  v-if="visitsCount"
                  class="bg-code-medical-center-nav flex size-[20px] shrink-0 items-center justify-center overflow-clip rounded-full p-[2px] shadow-[0px_0px_0px_4px_rgba(12,74,110,0.1)]"
                >
                  <span class="text-center text-[10px] font-sans font-medium leading-[14px] text-white whitespace-nowrap">
                    {{ visitsCount }}
                  </span>
                </span>
              </Link>

              <!-- Accessibility Button -->
              <AccessibilityWidget />

              <!-- Umów wizytę Button -->
              <a
                :href="bookAppointmentUrl || '#'"
                class="flex gap-[8px] h-[48px] items-center justify-center px-[20px] py-[14px] rounded-[8px] shadow-[0px_2px_6px_0px_rgba(16,24,40,0.06)] no-underline"
                style="background-image: linear-gradient(160.691deg, rgb(46, 168, 207) 0%, rgb(40, 182, 155) 100%)"
              >
                <div class="flex flex-col justify-center leading-0 text-[16px] text-center whitespace-nowrap text-white font-sans font-semibold">
                  <p class="leading-[18px]">{{ bookAppointmentName }}</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Services Dropdown (Konsultacje / Zabiegi / Badania) -->
    <div
      v-show="activeServiceCategory"
      ref="servicesDropdownRef"
      class="hidden lg:block absolute left-0 right-0 top-full bg-white border-b border-code-medical-center-secondary shadow-md z-50 w-full"
      @click.stop
    >
      <div class="max-w-[1200px] w-full mx-auto px-[24px] py-[24px]">
        <SpecialisationFilterList
          show-all-option
          :specialisations="specialisationsForFilter"
          :model-value="selectedServiceSpecialisationId"
          :loading="loadingSpecialisations"
          variant="header"
          reserve-min-height
          @update:model-value="filterServicesBySpecialisation"
        />

        <div class="w-full">
          <div
            v-if="loadingServices && filteredServices.length === 0"
            class="flex min-h-[200px] w-full items-center justify-center text-gray"
          >
            <p>{{ t('common.loading') }}</p>
          </div>

          <div
            v-else-if="filteredServices.length > 0"
            :class="filteredServices.length === 1
              ? 'flex w-fit mb-[24px]'
              : 'grid grid-cols-2 gap-[32px] w-full mb-[24px]'"
          >
            <SmallTextCard
              v-for="service in filteredServices.slice(0, 6)"
              :key="service.id"
              :href="`${servicesUrl}/${service.slug}`"
              :text="service.name"
            />
          </div>

          <div v-else class="flex min-h-[200px] w-full items-center justify-center text-gray">
            <p>{{ t('services.empty_hint') }}</p>
          </div>
        </div>

        <div class="w-full flex justify-center pt-[16px] pb-[8px]">
          <Link
            :href="servicesIndexHref"
            class="btn-nav-outline inline-flex h-12 items-center justify-center rounded-lg font-sans text-[16px] font-semibold leading-[18px] no-underline"
          >
            {{ activeServiceViewAllLabel }}
          </Link>
        </div>
      </div>
    </div>

    <!-- Specialists Dropdown Menu - Full Width -->
    <div
      v-show="showSpecialistsDropdown"
      ref="specialistsDropdownRef"
      class="hidden lg:block absolute left-0 right-0 top-full bg-white border-b border-code-medical-center-secondary shadow-md z-50 w-full"
      @click.stop
    >
      <div class="max-w-[1200px] w-full mx-auto px-[24px] py-[24px]">
        <SpecialisationFilterList
          show-all-option
          :specialisations="specialisationsForFilter"
          :model-value="selectedSpecialisationId"
          :loading="loadingSpecialisations"
          variant="header"
          reserve-min-height
          @update:model-value="filterBySpecialisation"
        />

        <div class="w-full min-h-[190px]">
          <div
            v-if="loadingSpecialists && filteredSpecialists.length === 0"
            class="flex min-h-[190px] w-full items-center justify-center text-gray"
          >
            <p>{{ t('common.loading') }}</p>
          </div>

          <div v-else-if="filteredSpecialists.length > 0" class="grid grid-cols-3 gap-[16px] w-full mb-[16px]">
            <SpecialistCard
              v-for="specialist in filteredSpecialists.slice(0, 6)"
              :key="specialist.id"
              :specialist="specialist"
              :specialists-base-url="siecialistUrl"
              clickable-card
            />
          </div>

          <div v-else class="flex min-h-[248px] w-full items-center justify-center text-gray">
            <p>{{ t('specialists.empty_hint') }}</p>
          </div>
        </div>

        <!-- View All Button -->
        <div class="w-full flex justify-center pt-[16px] pb-[8px]">
          <Link
            :href="siecialistUrl"
            class="btn-nav-outline inline-flex h-12 items-center justify-center rounded-lg font-sans text-[16px] font-semibold leading-[18px] no-underline"
          >
            {{ t('specialists.view_all') }}
          </Link>
        </div>
      </div>
    </div>

    <!-- Specializations Dropdown Menu - Full Width -->
    <div
      v-show="showSpecializationsDropdown"
      ref="specializationsDropdownRef"
      class="hidden lg:block absolute left-0 right-0 top-full bg-white border-b border-code-medical-center-secondary shadow-[0px_20px_40px_-8px_rgba(16,24,40,0.05),0px_20px_40px_-8px_rgba(16,24,40,0.1)] z-50 w-full min-h-[300px]"
      @click.stop
    >
      <div class="max-w-[1200px] w-full mx-auto px-[24px] py-[24px]">
        <div
          v-if="loadingSpecialisations && allSpecialisations.length === 0"
          class="flex min-h-[200px] w-full items-center justify-center text-gray"
        >
          <p>{{ t('common.loading') }}</p>
        </div>

        <!-- Specializations Grid - 6 per row -->
        <div v-else class="grid grid-cols-6 gap-[32px] w-full mb-[24px] min-h-[200px]">
          <SmallTextCard
            v-for="specialisation in allSpecialisations"
            :key="specialisation.id"
            :href="`${specialisationsUrl}/${specialisation.slug}`"
            :text="specialisation.name"
          />
        </div>

        <!-- View All Button -->
        <div class="w-full flex justify-center pt-[16px]">
          <Link
            :href="specialisationsUrl"
            class="btn-nav-outline inline-flex h-12 items-center justify-center rounded-lg font-sans text-[16px] font-semibold leading-[18px] no-underline"
          >
            {{ t('specialisations.view_all') }}
          </Link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Link, usePage, router } from '@inertiajs/vue3'
import { computed, ref, watch, watchEffect, nextTick, onMounted, onUnmounted } from 'vue'
import {
  mainHeaderPinningActive,
  mainHeaderRevealHeight,
  mainHeaderRevealed,
  resetStickyMainHeaderState,
} from '@/utils/stickyMainHeader'
import SpecialisationBadge from '@/Shared/SpecialisationBadge.vue'
import SmallTextCard from '@/Shared/SmallTextCard.vue'
import SpecialistCard from '@/Shared/SpecialistCard.vue'
import SpecialisationFilterList from '@/Shared/SpecialisationFilterList.vue'
import Icon from '@/Shared/Icon.vue'
import ContactPhoneRow from '@/Shared/ContactPhoneRow.vue'
import AccessibilityWidget from '@/Shared/AccessibilityWidget.vue'
import locationIcon from '@/images/components/location-marker.svg'
import chevronDownIcon from '@/images/components/cheveron-down.svg'
import flagPl from '@/images/components/flag-pl.svg'
import flagEn from '@/images/components/flag-en.svg'
import logoImage from '@/images/components/cmslogo-new.png'
import { useTranslations } from '@/utils/translations.js'
import { getBookings } from '@/utils/bookingStorage'
import { countHeaderVisits } from '@/utils/visitStatus'

const { t } = useTranslations()

// Liczba wizyt na plakietce przycisku "Twoje wizyty" - liczona z localStorage
// (wizyty nie sa juz wiazane z profilem lekarza). Zliczamy wizyty oplacone,
// umowione oraz oczekujace na platnosc (bez zakonczonych/bledych/anulowanych).
// Odswiezana przy powrocie do karty oraz zmianach w innej karcie (storage).
const visitsCount = ref(0)
const refreshVisitsCount = () => {
  visitsCount.value = countHeaderVisits(getBookings())
}

const props = defineProps({
  phoneNumber: {
    type: String,
  },
  phoneLabel: {
    type: String,
  },
  phoneHours: {
    type: String,
  },
  aktualnosciName: {
    type: String,
  },
  aktualnosciUrl: {
    type: String,
    default: '#',
  },
  contactUrl: {
    type: String,
    default: '#',
  },
  medicalConsultationsName: {
    type: String,
  },
  proceduresName: {
    type: String,
  },
  examinationsName: {
    type: String,
  },
  specialistsName: {
    type: String,
  },
  specializationsName: {
    type: String,
  },
  contactName: {
    type: String,
  },
  bookAppointmentName: {
    type: String,
  },
  bookAppointmentUrl: {
    type: String,
    default: '#',
  },
  infertilityClinicName: {
    type: String,
  },
  familyClinicName: {
    type: String,
  },
  instituteName: {
    type: String,
  },
  preview_mode: {
    type: Boolean,
    default: false,
  },
})

const page = usePage()
const currentUrl = computed(() => page.url)
const showLanguageDropdown = ref(false)
const languageDropdownRef = ref(null)
const mobileLanguageDropdownRef = ref(null)

// Specialists, services and specializations dropdown state
const showSpecialistsDropdown = ref(false)
const showSpecializationsDropdown = ref(false)
const activeServiceCategory = ref(null)
const mainNavRef = ref(null)
const specialistsDropdownRef = ref(null)
const specializationsDropdownRef = ref(null)
const servicesDropdownRef = ref(null)

// ---------------------------------------------------------------------------
// Reveal-on-scroll-up header. Once the user scrolls past the header it detaches
// (fixed) and hides; scrolling up brings it back so the main navigation is
// always one gesture away, even on pages with a sticky context tab menu.
// ---------------------------------------------------------------------------
const headerRootRef = ref(null)
const headerHeight = ref(0)
const headerShown = ref(true)
const pinReady = ref(false)

const pinningEnabled = computed(() => !props.preview_mode && pinReady.value)

// Animate via `transform` (excluded from CLS) instead of `top`. While the mobile
// drawer is open the header is fully revealed, so drop the transform to `none`
// to avoid establishing a containing block that would trap the drawer's
// `fixed inset-0` inside the header instead of the viewport.
const headerTransform = computed(() => {
  if (showMobileMenu.value) return 'none'
  return headerShown.value ? 'translateY(0)' : 'translateY(-100%)'
})

// Ignore tiny scroll deltas — sticky elements can nudge scrollY upward when they
// engage, which would otherwise flash the header back over sticky panels.
const HEADER_HIDE_SCROLL_DELTA = 8
// Minimum upward movement per frame to count toward reveal (filters sticky-panel nudges).
const HEADER_SHOW_SCROLL_STEP = 20
// Total intentional upward scroll (px) required before revealing the pinned header.
const HEADER_SHOW_SCROLL_DISTANCE = 180

let lastScrollY = 0
let upwardScrollAccumulated = 0
let scrollRafId = null
let resizeObserver = null

const measureHeaderHeight = () => {
  if (headerRootRef.value) {
    headerHeight.value = headerRootRef.value.offsetHeight
  }
}

const updateHeaderVisibility = () => {
  scrollRafId = null
  // Never react while the full-screen mobile drawer owns the viewport.
  if (showMobileMenu.value) return

  const y = window.scrollY || window.pageYOffset || 0
  const h = headerHeight.value

  if (y <= h) {
    // Near the top: behave like a normal in-flow header (always visible).
    headerShown.value = true
    upwardScrollAccumulated = 0
  } else if (y < lastScrollY - HEADER_SHOW_SCROLL_STEP) {
    upwardScrollAccumulated += lastScrollY - y
    if (upwardScrollAccumulated >= HEADER_SHOW_SCROLL_DISTANCE) {
      headerShown.value = true
      upwardScrollAccumulated = 0
    }
  } else if (y > lastScrollY + HEADER_HIDE_SCROLL_DELTA) {
    headerShown.value = false
    upwardScrollAccumulated = 0
  }

  lastScrollY = y
}

const onScroll = () => {
  if (scrollRafId !== null) return
  scrollRafId = window.requestAnimationFrame(updateHeaderVisibility)
}

watchEffect(() => {
  mainHeaderPinningActive.value = pinningEnabled.value
  mainHeaderRevealHeight.value = headerHeight.value
  mainHeaderRevealed.value = headerShown.value
})

// Data state
const allSpecialisations = ref([])
const specialisationsForFilter = ref([])
const filteredSpecialists = ref([])
const selectedSpecialisationId = ref(null)
const loadingSpecialists = ref(false)
const loadingSpecialisations = ref(false)

const filteredServices = ref([])
const selectedServiceSpecialisationId = ref(null)
const loadingServices = ref(false)

// In-memory caches so reopening a panel or switching filters does not
// collapse the layout with a loading flash.
const servicesDataCache = ref({})
const specialistsListCache = ref({})
const specialistsFilterSpecsCache = ref(null)
const specialisationsPreviewCache = ref(null)

const serviceViewAllLabel = (category) => {
  if (!category) return t('services.view_all.default')
  return t(`services.view_all.${category}`)
}

const serviceNavItems = computed(() => [
  { category: 'konsultacja_medyczna', label: props.medicalConsultationsName },
  { category: 'zabieg', label: props.proceduresName },
  { category: 'badanie', label: props.examinationsName },
])

const activeServiceViewAllLabel = computed(() => serviceViewAllLabel(activeServiceCategory.value))

// Language selector
const availableLocales = [
  {
    code: 'pl',
    flag: flagPl
  },
  {
    code: 'en',
    flag: flagEn
  }
]

const currentLocale = computed(() => {
  if (page.props && page.props.current_locale) {
    return page.props.current_locale
  }
  return 'pl'
})

const currentLocaleFlag = computed(() => {
  const locale = availableLocales.find(l => l.code === currentLocale.value)
  return locale ? locale.flag : availableLocales[0].flag
})

const canSwitchSiteLocale = computed(() => {
  if (props.preview_mode) return false;
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
    return false;
  }
  return true;
});

const toggleLanguageDropdown = () => {
  if (!canSwitchSiteLocale.value) return;
  showLanguageDropdown.value = !showLanguageDropdown.value;
};

const getCookie = (name) => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop().split(';').shift()
  return null
}

const getCsrfToken = () => {
  return getCookie('XSRF-TOKEN') || ''
}

const updateLocaleOnServer = async (locale) => {
  const csrfToken = getCsrfToken()
  const response = await fetch('/locale', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-XSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify({ locale }),
    credentials: 'same-origin',
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  return response.json()
}

const reloadPage = () => {
  const currentUrl = window.location.pathname
  router.visit(currentUrl, {
    preserveScroll: true,
  })
}

const handleLocaleSwitchSuccess = (data) => {
  if (data.success) {
    showLanguageDropdown.value = false
    reloadPage()
  } else {
    console.error('Locale switch failed:', data.error)
  }
}

const handleLocaleSwitchError = (error) => {
  console.error('Locale switch error:', error)
}

const switchLocale = (locale) => {
  if (!canSwitchSiteLocale.value) return;
  updateLocaleOnServer(locale)
    .then((data) => handleLocaleSwitchSuccess(data))
    .catch((error) => handleLocaleSwitchError(error));
};

const handleClickOutside = (event) => {
  const insideDesktopLang = languageDropdownRef.value && languageDropdownRef.value.contains(event.target)
  const insideMobileLang = mobileLanguageDropdownRef.value && mobileLanguageDropdownRef.value.contains(event.target)
  if (!insideDesktopLang && !insideMobileLang) {
    showLanguageDropdown.value = false
  }

  if (showSpecialistsDropdown.value && specialistsDropdownRef.value && !specialistsDropdownRef.value.contains(event.target)) {
    showSpecialistsDropdown.value = false
  }

  if (showSpecializationsDropdown.value && specializationsDropdownRef.value && !specializationsDropdownRef.value.contains(event.target)) {
    showSpecializationsDropdown.value = false
  }

  if (
    activeServiceCategory.value &&
    servicesDropdownRef.value &&
    !servicesDropdownRef.value.contains(event.target) &&
    !mainNavRef.value?.contains(event.target)
  ) {
    activeServiceCategory.value = null
  }
}

onMounted(() => {
  if (typeof window === 'undefined') return

  refreshVisitsCount()
  window.addEventListener('focus', refreshVisitsCount)
  window.addEventListener('storage', refreshVisitsCount)

  if (canSwitchSiteLocale.value) {
    document.addEventListener('click', handleClickOutside);
  }

  if (!props.preview_mode) {
    // Measure first, then pin: switching from in-flow to fixed while the spacer
    // takes over the same height keeps the layout perfectly stable.
    measureHeaderHeight()
    lastScrollY = window.scrollY || window.pageYOffset || 0
    // On a deep-linked load already scrolled past the header, start hidden so it
    // doesn't cover the target content; it will reveal on the first upward scroll.
    headerShown.value = lastScrollY <= headerHeight.value
    pinReady.value = true

    if (typeof ResizeObserver !== 'undefined' && headerRootRef.value) {
      resizeObserver = new ResizeObserver(measureHeaderHeight)
      resizeObserver.observe(headerRootRef.value)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', measureHeaderHeight)
  }
});

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    document.removeEventListener('click', handleClickOutside)
    window.removeEventListener('scroll', onScroll)
    window.removeEventListener('resize', measureHeaderHeight)
    window.removeEventListener('focus', refreshVisitsCount)
    window.removeEventListener('storage', refreshVisitsCount)
    if (scrollRafId !== null) window.cancelAnimationFrame(scrollRafId)
  }
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  resetStickyMainHeaderState()
  if (typeof document !== 'undefined') {
    document.body.style.overflow = ''
  }
})

const currentSiteContext = computed(() => {
  if (currentUrl.value.startsWith('/klinika-przyczynowego-leczenia-nieplodnosci')) {
    return 'infertility_treatment_clinic'
  } else if (currentUrl.value.startsWith('/instytut')) {
    return 'institute'
  } else if (currentUrl.value.startsWith('/klinika-zdrowia-rodziny')) {
    return 'family_clinic'
  }
  return 'code_medical_center'
})

// Resources are served only at the global, context-free paths.
const siecialistUrl = computed(() => '/specjalisci')
const specialisationsUrl = computed(() => '/specjalizacje')
const servicesUrl = computed(() => '/uslugi')

const servicesIndexHref = computed(() => {
  if (!activeServiceCategory.value) return servicesUrl.value
  return `${servicesUrl.value}?category=${encodeURIComponent(activeServiceCategory.value)}`
})

const serviceCacheKey = (category, specialisationId = null) =>
  specialisationId ? `${category}:${specialisationId}` : category

// Fetch specialisations linked to both active services (in category) and active specialists.
const fetchServiceCategoryData = async (category, specialisationId = null) => {
  const cacheKey = serviceCacheKey(category, specialisationId)
  const cached = servicesDataCache.value[cacheKey]
  if (cached) {
    if (!specialisationId) {
      specialisationsForFilter.value = cached.specialisations
    }
    filteredServices.value = cached.services
    return
  }

  const showSpecsLoading = !specialisationId && specialisationsForFilter.value.length === 0
  const showServicesLoading = filteredServices.value.length === 0
  if (showSpecsLoading) loadingSpecialisations.value = true
  if (showServicesLoading) loadingServices.value = true

  try {
    const csrfToken = getCsrfToken()
    let url = `/api/services?category=${encodeURIComponent(category)}`
    if (specialisationId) {
      url += `&specialisation_id=${specialisationId}`
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-XSRF-TOKEN': csrfToken,
      },
      credentials: 'same-origin',
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    const specialisations = data.specialisations || []
    const services = data.services || []

    servicesDataCache.value[cacheKey] = { specialisations, services }

    if (!specialisationId) {
      specialisationsForFilter.value = specialisations
    }
    filteredServices.value = services
  } catch (error) {
    console.error('Failed to fetch services:', error)
    if (!specialisationId) specialisationsForFilter.value = []
    filteredServices.value = []
  } finally {
    loadingSpecialisations.value = false
    loadingServices.value = false
  }
}

// Specialists dropdown — specs with at least one active service and one active specialist.
const fetchSpecialistsFilterSpecialisations = async () => {
  if (specialistsFilterSpecsCache.value) {
    specialisationsForFilter.value = specialistsFilterSpecsCache.value
    return
  }

  if (specialisationsForFilter.value.length === 0) {
    loadingSpecialisations.value = true
  }

  try {
    const csrfToken = getCsrfToken()
    const response = await fetch('/api/specialisations?with_specialists=1', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-XSRF-TOKEN': csrfToken,
      },
      credentials: 'same-origin',
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    const specialisations = data.specialisations || []
    specialistsFilterSpecsCache.value = specialisations
    specialisationsForFilter.value = specialisations
  } catch (error) {
    console.error('Failed to fetch specialisations:', error)
    specialisationsForFilter.value = []
  } finally {
    loadingSpecialisations.value = false
  }
}

// Specjalizacje tab — preview of first 10 (alphabetical).
const fetchSpecialisationsPreview = async () => {
  if (specialisationsPreviewCache.value) {
    allSpecialisations.value = specialisationsPreviewCache.value
    return
  }

  if (allSpecialisations.value.length === 0) {
    loadingSpecialisations.value = true
  }

  try {
    const csrfToken = getCsrfToken()
    const response = await fetch('/api/specialisations?limit=12', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-XSRF-TOKEN': csrfToken,
      },
      credentials: 'same-origin',
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    const specialisations = data.specialisations || []
    specialisationsPreviewCache.value = specialisations
    allSpecialisations.value = specialisations
  } catch (error) {
    console.error('Failed to fetch specialisations:', error)
    allSpecialisations.value = []
  } finally {
    loadingSpecialisations.value = false
  }
}

// Fetch specialists with optional specialisation filter
const fetchSpecialists = async (specialisationId = null) => {
  const cacheKey = String(specialisationId ?? 'all')
  const cached = specialistsListCache.value[cacheKey]
  if (cached) {
    filteredSpecialists.value = cached
    return
  }

  if (filteredSpecialists.value.length === 0) {
    loadingSpecialists.value = true
  }

  try {
    const csrfToken = getCsrfToken()
    let url = '/api/specialists'
    if (specialisationId) {
      url += `?specialisation_id=${specialisationId}`
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-XSRF-TOKEN': csrfToken,
      },
      credentials: 'same-origin',
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    const specialists = data.specialists || []
    specialistsListCache.value[cacheKey] = specialists
    filteredSpecialists.value = specialists
  } catch (error) {
    console.error('Failed to fetch specialists:', error)
    filteredSpecialists.value = []
  } finally {
    loadingSpecialists.value = false
  }
}

const fetchServicesForCategory = async (category, specialisationId) => {
  await fetchServiceCategoryData(category, specialisationId)
}

const toggleServiceDropdown = async (category) => {
  if (activeServiceCategory.value === category) {
    activeServiceCategory.value = null
    return
  }

  activeServiceCategory.value = category
  showSpecialistsDropdown.value = false
  showSpecializationsDropdown.value = false
  showLanguageDropdown.value = false

  const cached = servicesDataCache.value[category]
  if (cached) {
    specialisationsForFilter.value = cached.specialisations
    filteredServices.value = cached.services
    selectedServiceSpecialisationId.value = null
    return
  }

  await fetchServiceCategoryData(category)
  selectedServiceSpecialisationId.value = null
}

const filterServicesBySpecialisation = async (specialisationId) => {
  if (!activeServiceCategory.value) return
  selectedServiceSpecialisationId.value = specialisationId
  await fetchServicesForCategory(activeServiceCategory.value, specialisationId)
}

const toggleSpecialistsDropdown = async () => {
  showSpecialistsDropdown.value = !showSpecialistsDropdown.value

  if (showSpecialistsDropdown.value) {
    activeServiceCategory.value = null
    showSpecializationsDropdown.value = false
    showLanguageDropdown.value = false

    await fetchSpecialistsFilterSpecialisations()

    selectedSpecialisationId.value = null
    fetchSpecialists(null)
  }
}

const toggleSpecializationsDropdown = () => {
  showSpecializationsDropdown.value = !showSpecializationsDropdown.value

  if (showSpecializationsDropdown.value) {
    activeServiceCategory.value = null
    showSpecialistsDropdown.value = false
    showLanguageDropdown.value = false

    fetchSpecialisationsPreview()
  }
}

const filterBySpecialisation = async (specialisationId) => {
  selectedSpecialisationId.value = specialisationId
  await fetchSpecialists(specialisationId)
}

// ---------------------------------------------------------------------------
// Mobile menu (< lg). Reuses the same data-fetching + Shared components as the
// desktop dropdowns; only the presentation (full-screen drawer + drill-down)
// is mobile-specific. Kept on separate state so document click handlers and the
// desktop dropdowns never interfere with the drawer.
// ---------------------------------------------------------------------------
const showMobileMenu = ref(false)
const mobileView = ref('main')
const mobileServiceCategory = ref(null)
const mobileScrollRef = ref(null)

// Reset the shared drawer scroll container to the top whenever the view
// changes, so drill-down views never open pre-scrolled from the main list.
watch(mobileView, () => {
  nextTick(() => {
    if (mobileScrollRef.value) mobileScrollRef.value.scrollTop = 0
  })
})

const mobileServiceLabel = computed(
  () => serviceNavItems.value.find((item) => item.category === mobileServiceCategory.value)?.label ?? ''
)

const mobileServicesIndexHref = computed(() => {
  if (!mobileServiceCategory.value) return servicesUrl.value
  return `${servicesUrl.value}?category=${encodeURIComponent(mobileServiceCategory.value)}`
})

const mobileServiceViewAllLabel = computed(() => serviceViewAllLabel(mobileServiceCategory.value))

const openMobileMenu = () => {
  showMobileMenu.value = true
  mobileView.value = 'main'
}

const closeMobileMenu = () => {
  showMobileMenu.value = false
  mobileView.value = 'main'
}

const openMobileService = async (category) => {
  mobileServiceCategory.value = category
  mobileView.value = 'service'
  await fetchServiceCategoryData(category)
  selectedServiceSpecialisationId.value = null
}

const mobileFilterServices = async (specialisationId) => {
  selectedServiceSpecialisationId.value = specialisationId
  await fetchServiceCategoryData(mobileServiceCategory.value, specialisationId)
}

const openMobileSpecialists = async () => {
  mobileView.value = 'specialists'
  await fetchSpecialistsFilterSpecialisations()
  selectedSpecialisationId.value = null
  fetchSpecialists(null)
}

const mobileFilterSpecialists = async (specialisationId) => {
  selectedSpecialisationId.value = specialisationId
  await fetchSpecialists(specialisationId)
}

const openMobileSpecializations = () => {
  mobileView.value = 'specializations'
  fetchSpecialisationsPreview()
}

// Lock background scroll while the full-screen drawer is open.
watch(showMobileMenu, (open) => {
  if (typeof document === 'undefined') return
  document.body.style.overflow = open ? 'hidden' : ''
})
</script>

<style scoped>
</style>
